import React, { Suspense, lazy } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAddressCard,
  faAngleDoubleDown,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
  faArrowDown,
  faBars,
  faBell,
  faCheck,
  faCoins,
  faChevronDown,
  faChevronUp,
  faCircleUser,
  faClinicMedical,
  faClock,
  faCog,
  faColumns,
  faComment,
  faComments,
  faCopy,
  faCrown,
  faCube,
  faCubes,
  faDownload,
  faEllipsisH,
  faEllipsisV,
  faEnvelope,
  faExpand,
  faEye,
  faEyeSlash,
  faFileAlt,
  faFilm,
  faFilter,
  faFire,
  faFolder,
  faForwardFast,
  faGavel,
  faGlasses,
  faHeadset,
  faHeart,
  faHeartbeat,
  faHeartBroken,
  faHistory,
  faHome,
  faImages,
  faLanguage,
  faLink,
  faList,
  faLock,
  faMoon,
  faPalette,
  faPause,
  faPenToSquare,
  faPlay,
  faPlusCircle,
  faPlusSquare,
  faPoll,
  faQuestionCircle,
  faRedo,
  faRightFromBracket,
  faSearch,
  faShare,
  faShareNodes,
  faShield,
  faSignOutAlt,
  faSquare,
  faStar,
  faTags,
  faThumbtack,
  faTicket,
  faTimes,
  faTrash,
  faUser,
  faUserCheck,
  faUserCircle,
  faUserClock,
  faUserCog,
  faUserGroup,
  faUserMinus,
  faUserPlus,
  faUserSlash,
  faVideo,
  faVolumeHigh,
  faVolumeLow,
  faVolumeMute,
  faVolumeOff,
  faXmark,
  faYenSign,
  faGift,
  faSun,
  faCalendarDays,
} from "@fortawesome/free-solid-svg-icons";

import {
  faDiscord,
  faPatreon,
  faTwitter,
} from "@fortawesome/free-brands-svg-icons";

import Text from "components/Text";
import "./style.scss";

// https://fontawesome.com/icons

const icons = {
  address: faAddressCard,
  arrowDoubleDown: faAngleDoubleDown,
  arrowDown: faArrowDown,
  bell: faBell,
  calendar: faCalendarDays,
  check: faCheck,
  chevronDown: faChevronDown,
  chevronUp: faChevronUp,
  clock: faClock,
  close: faTimes,
  crown: faCrown,
  cog: faCog,
  content: faFolder,
  copy: faCopy,
  create: faPlusSquare,
  dashboard: faColumns,
  dark: faMoon,
  discord: faDiscord,
  document: faFileAlt,
  donate: faGift,
  doubleLeft: faAngleDoubleLeft,
  doubleRight: faAngleDoubleRight,
  download: faDownload,
  ellipsisH: faEllipsisH,
  ellipsisV: faEllipsisV,
  envelope: faEnvelope,
  expand: faExpand,
  eye: faEye,
  eyeSlash: faEyeSlash,
  filter: faFilter,
  film: faFilm,
  fire: faFire,
  forum: faComments,
  friend: faUserPlus,
  friends: faUserGroup,
  friendPending: faUserClock,
  follow: faCheck,
  gallery: faImages,
  glasses: faGlasses,
  heart: faHeart,
  heartbeat: faHeartbeat,
  heartBroken: faHeartBroken,
  help: faQuestionCircle,
  history: faHistory,
  house: faHome,
  image: faImages,
  language: faLanguage,
  left: faAngleLeft,
  light: faSun,
  link: faLink,
  list: faList,
  lock: faLock,
  logout: faRightFromBracket,
  menu: faBars,
  moon: faMoon,
  page: faFileAlt,
  patreon: faPatreon,
  pause: faPause,
  pen: faPenToSquare,
  play: faPlay,
  playlist: faForwardFast,
  plus: faPlusCircle,
  poll: faPoll,
  post: faFileAlt,
  premium: faStar,
  preview: faPalette,
  product: faCube,
  products: faCubes,
  profile: faCircleUser,
  queue: faClock,
  rating: faAddressCard,
  replay: faRedo,
  right: faAngleRight,
  rule: faGavel,
  search: faSearch,
  share: faShare,
  share: faShareNodes,
  shield: faShield,
  signOut: faSignOutAlt,
  square: faSquare,
  standing: faClinicMedical,
  star: faStar,
  star: faStar,
  sticky: faThumbtack,
  support: faHeadset,
  subscriptions: faUserCheck,
  tags: faTags,
  theme: faPalette,
  thread: faComment,
  threads: faComments,
  trash: faTrash,
  twitter: faTwitter,
  unfriend: faUserMinus,
  unfollow: faXmark,
  user: faUserCircle,
  userBlock: faUserSlash,
  userCog: faUserCog,
  userUnblock: faUser,
  video: faVideo,
  volumeHigh: faVolumeHigh,
  volumeLow: faVolumeLow,
  volumeMuted: faVolumeMute,
  volumeNone: faVolumeOff,
  voucher: faTicket,
  yen: faYenSign,
  wura: faCoins,
};

function IconWrapper({ inline, children, ...props }) {
  if (inline) {
    return <span {...props}>{children}</span>;
  }

  return <div {...props}>{children}</div>;
}

function Icon({ name, color, alerts, inline, title, className }) {
  const classes = classnames("icon", className, {
    [`icon--${color}`]: color !== "inherit",
    [`icon--inline`]: inline,
  });

  const numAlerts = alerts ? `${Math.min(99, alerts)}${alerts > 99 ? "+" : ""}` : 0;

  return (
    <Suspense fallback={null}>
      <IconWrapper className={classes} inline={inline} title={title}>
        <FontAwesomeIcon icon={icons[name]} />
        {(alerts !== null && alerts !== 0) &&
          <Text size="tiny" className="icon__alerts">{numAlerts}</Text>
        }
      </IconWrapper>
    </Suspense>
  );
};

Icon.defaultProps = {
  size: "normal",
  color: "inherit",
  inline: false,
  alerts: null,
  className: null,
};

Icon.propTypes = {
  name: PropTypes.string.isRequired,
  inline: PropTypes.bool,
  title: PropTypes.string,
  alerts: PropTypes.any,
  size: PropTypes.oneOf(["small", "normal", "medium", "large"]),
  color: PropTypes.oneOf(["inherit", "primary", "secondary", "text", "red", "green", "yellow", "blue", "white", "premium", "wura"]),
  className: PropTypes.string,
};

export default Icon;
